import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Building2, CheckCircle2, Loader2 } from 'lucide-react';
import useStore from '@/store/useStore';
import { apiGetGuestRestaurants, apiGuestLogin, apiGuestSwitchStore } from '@/services/api';
import { getSelectedMerchantId, setSelectedMerchantId, storeRoute } from '@/lib/routes';

const PENDING_GUEST_PHONE_KEY = 'flyback_pending_guest_phone';
const PENDING_GUEST_OTP_KEY = 'flyback_pending_guest_otp';
const ENTRY_MERCHANT_KEY = 'flyback_entry_merchant_id';
const ENTRY_MERCHANT_KEY_V2 = 'entry_merchant_id';

export default function RestaurantSelectionPage() {
  const navigate = useNavigate();
  const {
    guestTheme,
    guestLogin,
    setCurrentMerchantId,
    setStoreInfo,
    setMerchantInfo,
    setWallet,
    setTransactions,
    guestToken,
    guestUser,
  } = useStore();

  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingId, setSubmittingId] = useState('');
  const [error, setError] = useState('');

  const pendingPhone = useMemo(
    () => window.sessionStorage.getItem(PENDING_GUEST_PHONE_KEY) || String(guestUser?.phone || '').trim(),
    [guestUser?.phone]
  );
  const pendingOtp = useMemo(() => window.sessionStorage.getItem(PENDING_GUEST_OTP_KEY) || '', []);

  const loadRestaurants = useCallback(async () => {
    if (!pendingPhone) {
      navigate('/store/login', { replace: true });
      return;
    }

    setLoading(true);
    setError('');
    try {
      const list = await apiGetGuestRestaurants(pendingPhone);
      setRestaurants(list);
      if (!list.length) {
        setError('No restaurants are linked with this phone number.');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Unable to load restaurants right now.');
    } finally {
      setLoading(false);
    }
  }, [navigate, pendingPhone]);

  useEffect(() => {
    if (guestToken) {
      const selectedMerchantId = getSelectedMerchantId();
      if (selectedMerchantId) {
        navigate(storeRoute(selectedMerchantId, 'wallet'), { replace: true });
        return;
      }
      if (!pendingPhone) {
        navigate('/store/login', { replace: true });
        return;
      }
    } else if (!pendingPhone) {
      navigate('/store/login', { replace: true });
      return;
    }
    loadRestaurants();
  }, [guestToken, loadRestaurants, navigate, pendingPhone]);

  const handleSelectRestaurant = async (merchantId) => {
    const selectedId = String(merchantId || '').trim();
    if (!selectedId || submittingId) return;
    if (!pendingPhone) {
      navigate('/store/login', { replace: true });
      return;
    }
    if (!guestToken && !pendingOtp) {
      navigate('/store/login', { replace: true });
      return;
    }
    setSubmittingId(selectedId);
    setError('');

    try {
      // Clear stale context first so we never bounce to previous store.
      setWallet(null);
      setTransactions([]);
      setStoreInfo(null);
      setMerchantInfo(null);
      setCurrentMerchantId(selectedId);
      window.localStorage.removeItem('selected_merchant_id');
      setSelectedMerchantId(selectedId);

      const result = guestToken
        ? await apiGuestSwitchStore(selectedId)
        : await apiGuestLogin(pendingPhone, pendingOtp, '', selectedId);
      guestLogin(result.token, result.customer, result.merchant);
      if (!guestToken) {
        window.sessionStorage.removeItem(PENDING_GUEST_OTP_KEY);
      }

      window.localStorage.removeItem(ENTRY_MERCHANT_KEY);
      window.localStorage.removeItem(ENTRY_MERCHANT_KEY_V2);
      navigate(storeRoute(selectedId, 'wallet'), { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Unable to continue with this restaurant.');
    } finally {
      setSubmittingId('');
    }
  };

  return (
    <div className={`${guestTheme === 'dark' ? 'dark ' : ''}min-h-screen bg-[#faf5f5] dark:bg-background px-6 py-8`} data-testid="restaurant-selection-page">
      <div className="mx-auto max-w-md space-y-6">
        <div className="text-center">
          <div className="w-14 h-14 rounded-2xl gradient-brand flex items-center justify-center mx-auto">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <h1 className="mt-3 text-2xl font-bold tracking-tight" style={{ fontFamily: "'Bricolage Grotesque', sans-serif" }}>
            Choose Your Restaurant
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Select a restaurant linked to your phone number.
          </p>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-border bg-card p-8 flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading restaurants...
          </div>
        ) : (
          <div className="space-y-3">
            {restaurants.map((restaurant) => (
              <motion.button
                key={restaurant.id}
                whileTap={{ scale: 0.99 }}
                onClick={() => handleSelectRestaurant(restaurant.id)}
                disabled={Boolean(submittingId)}
                className="w-full rounded-2xl border border-border bg-card p-4 text-left hover:border-brand/40 hover:bg-brand-soft/20 transition-colors disabled:opacity-60"
                data-testid={`restaurant-option-${restaurant.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="font-semibold text-base text-stone-900 dark:text-white">{restaurant.restaurant_name}</p>
                    <p className="text-xs text-stone-500 dark:text-stone-300 mt-1">Tap to continue</p>
                  </div>
                  {submittingId === restaurant.id ? (
                    <Loader2 className="w-4 h-4 animate-spin text-brand" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 text-brand" />
                  )}
                </div>
              </motion.button>
            ))}

            {!restaurants.length && (
              <div className="rounded-2xl border border-border bg-card p-4 text-sm text-muted-foreground text-center">
                No associated restaurants found.
              </div>
            )}
          </div>
        )}

        {error && <p className="text-sm text-destructive text-center">{error}</p>}
      </div>
    </div>
  );
}
